import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Fragment, useState } from 'react';
import {
  Box,
  Package,
  ShieldCheck,
  AlertTriangle,
  Truck,
  ArrowRight,
  Warehouse,
  Layers,
  ChevronDown,
  ChevronRight,
} from '@/lib/icons';

import {
  getInventorySummary,
  getShipments,
  getLowStock,
} from '@/features/inventory/api';
import { useBranchesQuery } from '@/features/branches/queries';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/dates';

export const Route = createFileRoute('/_layout/inventory/')({
  component: InventoryDashboardPage,
});

const shipmentStatusBadge = (status: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    dispatched: {
      cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
      label: 'Dispatched',
    },
    received: {
      cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
      label: 'Received',
    },
    cancelled: {
      cls: 'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
      label: 'Cancelled',
    },
  };
  const s = map[status] ?? { cls: '', label: status };
  return <Badge className={s.cls}>{s.label}</Badge>;
};

function InventoryDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Enrolling stock into inventory is a tenant-admin act: only the
  // tenant-admin role template carries `device_units:create`, and
  // offerings-service requires it on POST /bulk-enroll. A branch manager
  // reads and moves stock but does not bring it into the tenant.
  const canEnroll = useAuth().hasPermission('device_units', 'create');

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['inventory-summary'],
    queryFn: getInventorySummary,
  });

  // Keyed UNDER `['shipments']`, not alongside it as `['shipments-recent']`:
  // TanStack invalidation is prefix-matched, so a sibling key is unreachable
  // from `invalidateQueries({ queryKey: ['shipments'] })` and this card kept
  // showing a transfer as still dispatched after it had been received.
  const { data: transfersData, isLoading: transfersLoading } = useQuery({
    queryKey: ['shipments', 'recent'],
    queryFn: () => getShipments({ limit: 5 }),
  });

  const { data: lowStockData, isLoading: lowStockLoading } = useQuery({
    queryKey: ['inventory-low-stock'],
    queryFn: () => getLowStock(5),
  });

  const { data: branchesData } = useBranchesQuery();

  const summary = summaryData?.value;
  const recentTransfers = transfersData?.value ?? [];
  const lowStockItems = lowStockData?.value?.data ?? [];
  const branches = branchesData?.value ?? [];

  // Compute aggregate stats from summary
  const totalInStock =
    summary?.data.reduce((sum, v) => sum + v.totalInStock, 0) ?? 0;
  const totalReserved =
    summary?.data.reduce((sum, v) => sum + v.totalReserved, 0) ?? 0;
  const totalFinanced =
    summary?.data.reduce((sum, v) => sum + v.totalFinanced, 0) ?? 0;
  const totalUnits = totalInStock + totalReserved + totalFinanced;

  // Compute stock by branch from summary data
  const branchStockMap = new Map<
    string,
    { name: string; inStock: number; reserved: number; financed: number }
  >();
  summary?.data.forEach((variant) => {
    variant.stockByBranch.forEach((bs) => {
      const existing = branchStockMap.get(bs.branchId);
      if (existing) {
        existing.inStock += bs.inStock;
        existing.reserved += bs.reserved;
        existing.financed += bs.financed;
      } else {
        branchStockMap.set(bs.branchId, {
          name: bs.branchName,
          inStock: bs.inStock,
          reserved: bs.reserved,
          financed: bs.financed,
        });
      }
    });
  });
  const branchStockList = Array.from(branchStockMap.entries()).map(
    ([id, data]) => ({ id, ...data })
  );

  // Group variants by model for the stock breakdown table
  const modelGroups = new Map<
    string,
    {
      modelId: string;
      brand: string;
      modelName: string;
      variants: Array<{
        variantId: string;
        storageCapacity: string | null;
        color: string | null;
        sku: string | null;
        totalInStock: number;
        totalReserved: number;
        totalFinanced: number;
        totalActive: number;
        totalLocked: number;
      }>;
      totalInStock: number;
      totalReserved: number;
      totalFinanced: number;
      totalActive: number;
      totalLocked: number;
    }
  >();
  summary?.data.forEach((v) => {
    const existing = modelGroups.get(v.modelId);
    const variantEntry = {
      variantId: v.variantId,
      storageCapacity: v.storageCapacity,
      color: v.color,
      sku: v.sku,
      totalInStock: v.totalInStock,
      totalReserved: v.totalReserved,
      totalFinanced: v.totalFinanced,
      totalActive: v.totalActive,
      totalLocked: v.totalLocked,
    };
    if (existing) {
      existing.variants.push(variantEntry);
      existing.totalInStock += v.totalInStock;
      existing.totalReserved += v.totalReserved;
      existing.totalFinanced += v.totalFinanced;
      existing.totalActive += v.totalActive;
      existing.totalLocked += v.totalLocked;
    } else {
      modelGroups.set(v.modelId, {
        modelId: v.modelId,
        brand: v.brand,
        modelName: v.modelName,
        variants: [variantEntry],
        totalInStock: v.totalInStock,
        totalReserved: v.totalReserved,
        totalFinanced: v.totalFinanced,
        totalActive: v.totalActive,
        totalLocked: v.totalLocked,
      });
    }
  });
  const modelStockList = Array.from(modelGroups.values());

  const stats = [
    {
      label: t('inventory.stats.totalUnits'),
      value: totalUnits,
      icon: Box,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: t('inventory.stats.inStock'),
      value: totalInStock,
      icon: Package,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: t('inventory.stats.financed'),
      value: totalFinanced,
      icon: ShieldCheck,
      color: 'from-violet-500 to-violet-600',
    },
    {
      label: t('inventory.stats.reserved'),
      value: totalReserved,
      icon: AlertTriangle,
      color: 'from-amber-500 to-amber-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('inventory.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('inventory.description')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/inventory/units' })}
          >
            {t('inventory.units.title')}
          </Button>
          {canEnroll && (
            <Button
              className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
              onClick={() => navigate({ to: '/inventory/units/enroll' })}
            >
              {t('inventory.units.enrollUnit')}
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {summaryLoading ? '—' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stock by Model/Variant */}
      <StockByModelCard
        modelStockList={modelStockList}
        isLoading={summaryLoading}
        t={t}
      />

      {/* Two-column layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Stock by Branch */}
        <Card className="shadow-md border-0">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Warehouse className="h-5 w-5" />
                {t('inventory.dashboard.stockByBranch')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {summaryLoading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : branchStockList.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Warehouse className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('common.noData')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>{t('common.form.branch')}</TableHead>
                    <TableHead className="text-right">
                      {t('inventory.stats.inStock')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.stats.reserved')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.stats.financed')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {branchStockList.map((branch) => (
                    <TableRow key={branch.id}>
                      <TableCell className="font-medium">
                        {branch.name}
                      </TableCell>
                      <TableCell className="text-right">
                        {branch.inStock}
                      </TableCell>
                      <TableCell className="text-right">
                        {branch.reserved}
                      </TableCell>
                      <TableCell className="text-right">
                        {branch.financed}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Low Stock Alerts */}
        <Card className="shadow-md border-0">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('inventory.dashboard.lowStockAlerts')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {lowStockLoading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : lowStockItems.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('inventory.lowStock.noAlerts')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>{t('inventory.lowStock.variant')}</TableHead>
                    <TableHead className="text-right">
                      {t('inventory.lowStock.inStock')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.lowStock.threshold')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lowStockItems.map((item) => (
                    <TableRow key={item.variantId}>
                      <TableCell>
                        <div>
                          <span className="font-medium">
                            {item.brand} {item.modelName}
                          </span>
                          <span className="text-muted-foreground text-xs ml-2">
                            {[item.storageCapacity, item.color]
                              .filter(Boolean)
                              .join(' / ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-semibold text-amber-600 dark:text-amber-400">
                          {item.inStockCount}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {item.threshold}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Transfers */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Truck className="h-5 w-5" />
              {t('inventory.dashboard.recentTransfers')}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/inventory/transfers' })}
            >
              View All <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {transfersLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : recentTransfers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Truck className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('inventory.transfers.empty.title')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Units</TableHead>
                  <TableHead>
                    {t('inventory.transfers.columns.status')}
                  </TableHead>
                  <TableHead>Dispatched</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentTransfers.map((shipment) => (
                  <TableRow key={shipment.id}>
                    <TableCell>
                      {shipment.fromBranchName ?? 'External'}
                    </TableCell>
                    <TableCell>{shipment.toBranchName ?? '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {shipment.itemCount} unit{shipment.itemCount !== 1 ? 's' : ''}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {shipmentStatusBadge(shipment.status)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(shipment.dispatchedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Stock by Model/Variant card ─────────────────────────────────

type ModelStock = {
  modelId: string;
  brand: string;
  modelName: string;
  variants: Array<{
    variantId: string;
    storageCapacity: string | null;
    color: string | null;
    sku: string | null;
    totalInStock: number;
    totalReserved: number;
    totalFinanced: number;
    totalActive: number;
    totalLocked: number;
  }>;
  totalInStock: number;
  totalReserved: number;
  totalFinanced: number;
  totalActive: number;
  totalLocked: number;
};

function StockByModelCard({
  modelStockList,
  isLoading,
  t,
}: Readonly<{
  modelStockList: ModelStock[];
  isLoading: boolean;
  t: (key: string) => string;
}>) {
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());

  const toggleModel = (modelId: string) => {
    setExpandedModels((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) {
        next.delete(modelId);
      } else {
        next.add(modelId);
      }
      return next;
    });
  };

  return (
    <Card className="shadow-md border-0">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Layers className="h-5 w-5" />
          {t('inventory.dashboard.stockByModel')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : modelStockList.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Layers className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">{t('common.noData')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="w-8" />
                <TableHead>{t('inventory.dashboard.modelVariant')}</TableHead>
                <TableHead className="text-right">
                  {t('inventory.stats.inStock')}
                </TableHead>
                <TableHead className="text-right">
                  {t('inventory.stats.reserved')}
                </TableHead>
                <TableHead className="text-right">
                  {t('inventory.stats.financed')}
                </TableHead>
                <TableHead className="text-right">
                  {t('inventory.stats.active')}
                </TableHead>
                <TableHead className="text-right">
                  {t('inventory.stats.locked')}
                </TableHead>
                <TableHead className="text-right">
                  {t('inventory.stats.totalUnits')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {modelStockList.map((model) => {
                const isExpanded = expandedModels.has(model.modelId);
                const modelTotal =
                  model.totalInStock +
                  model.totalReserved +
                  model.totalFinanced;
                const hasVariants = model.variants.length > 1;

                return (
                  <Fragment key={model.modelId}>
                    <TableRow
                      className={
                        hasVariants
                          ? 'cursor-pointer hover:bg-muted/30 transition-colors'
                          : ''
                      }
                      onClick={() =>
                        hasVariants && toggleModel(model.modelId)
                      }
                    >
                      <TableCell className="w-8 px-2">
                        {hasVariants &&
                          (isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          ))}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {model.brand} {model.modelName}
                        </span>
                        {!hasVariants && model.variants[0] && (
                          <span className="text-muted-foreground text-xs ml-2">
                            {[
                              model.variants[0].storageCapacity,
                              model.variants[0].color,
                            ]
                              .filter(Boolean)
                              .join(' / ')}
                          </span>
                        )}
                        {hasVariants && (
                          <span className="text-muted-foreground text-xs ml-2">
                            ({model.variants.length} variants)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className="text-emerald-600 dark:text-emerald-400">
                          {model.totalInStock}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className="text-amber-600 dark:text-amber-400">
                          {model.totalReserved}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className="text-blue-600 dark:text-blue-400">
                          {model.totalFinanced}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className="text-sky-600 dark:text-sky-400">
                          {model.totalActive}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className="text-rose-600 dark:text-rose-400">
                          {model.totalLocked}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {modelTotal}
                      </TableCell>
                    </TableRow>
                    {isExpanded &&
                      model.variants.map((variant) => {
                        const variantTotal =
                          variant.totalInStock +
                          variant.totalReserved +
                          variant.totalFinanced;
                        return (
                          <TableRow
                            key={variant.variantId}
                            className="bg-muted/20"
                          >
                            <TableCell />
                            <TableCell className="pl-10">
                              <span className="text-sm text-muted-foreground">
                                {[variant.storageCapacity, variant.color]
                                  .filter(Boolean)
                                  .join(' / ') || 'Default'}
                                {variant.sku && (
                                  <span className="ml-2 font-mono text-xs">
                                    ({variant.sku})
                                  </span>
                                )}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              {variant.totalInStock}
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              {variant.totalReserved}
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              {variant.totalFinanced}
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              {variant.totalActive}
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              {variant.totalLocked}
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium">
                              {variantTotal}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
